import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FiEdit3, FiFlag, FiCopy, FiUsers } from 'react-icons/fi';
import { useCopyMove } from '../CopyMoveContext';
import { useMoodleBadgeFlags } from './StudentFlags/MoodleBadgeFlags';
import './MovableBar.css';
import './StudentFlags/MoodleBadgeFlags.css';

const MovableBar = ({ 
  id, 
  uuid,
  initialX = 0, 
  initialY = 0, 
  initialWidth = 150, 
  initialLabel = "Neuer Balken", 
  startDate,
  endDate,
  onDelete, 
  onUpdate,
  onEdit,
  onMoveUp,
  onMoveDown,
  isEditing = false,
  isFirst = false,
  isLast = false,
  visibleWeeks = 10,
  weekWidth = 200,
  textColor = '#ffffff',
  backgroundColor = '#007bff',
  borderStyle = 'solid',
  borderColor = '#0056b3',
  borderThickness = 1,
  linkEvidence = false,
  evidenceType = 'moodle-badge',
  evidenceLabel = '',
  evidenceDueDays = 30,
  evidenceData = {},
  learnerBadges = [],
  hasEditPermission = false,
  showInfoPanel = false,
  onToggleInfoPanel = null,
  user = null,
  isSelected = false,
  onSelectionChange = null,
  showSelection = false,
  panelId,
  panelTitle,
  panelGroupTitle,
  assignedStudents = []
}) => {
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const [width, setWidth] = useState(initialWidth);
  const [label, setLabel] = useState(initialLabel);
  const [isEditingLabel] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDraggingFlag, setIsDraggingFlag] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [flagPosition, setFlagPosition] = useState(50); // Initialisiere mit 50% (Mitte)
  const flagPositionRef = useRef(50); // Ref für die aktuelle Flag-Position
  const lastEvidenceDueDaysRef = useRef(evidenceDueDays); // Ref um zu tracken ob evidenceDueDays geändert wurde
  const barRef = useRef(null);
  const labelRef = useRef(null);
  
  // Refs to store the current values during drag/resize
  const currentPositionRef = useRef({ x: initialX, y: initialY });
  const currentWidthRef = useRef(initialWidth);

    // Copy-Move Funktionalität
  const { openCopyMoveDialog } = useCopyMove();

  // Für Lernende: Erstelle ein Student-Array mit nur dem aktuellen Benutzer
  const learnerStudentData = React.useMemo(() => {
    if (!user || !user.isLearner) return [];
    
    const currentUserLogin = user?.username || user?.login || user?.id;
    
    // Prüfe ob der Lernende diesem Task zugewiesen ist
    const assignedStudent = assignedStudents.find(student => 
      student.login === currentUserLogin || 
      student.username === currentUserLogin ||
      student.id === currentUserLogin
    );
    
    if (assignedStudent) {
      return [assignedStudent]; // Verwende die echten Zuordnungsdaten
    }
    
    // Fallback: Mock-Student-Objekt für nicht-zugewiesene Lernende
    return [{
      id: currentUserLogin,
      login: currentUserLogin,
      username: currentUserLogin,
      vorname: user?.name?.split(' ')[0] || 'Aktueller',
      nachname: user?.name?.split(' ').slice(1).join(' ') || 'Benutzer',
      klasse: 'Nicht zugeordnet',
      email: user?.email || ''
    }];
  }, [user, assignedStudents]);

  // Task-Daten für Badge-Anzeige vorbereiten
  const taskDataForBadges = React.useMemo(() => {
    const result = (!user?.isLearner || !linkEvidence || evidenceType !== 'moodle-badge') ? null : (() => {
      // Berechne viewStartDate (Montag der aktuellen Woche aus globalem startDate)
      const viewStartDate = new Date(startDate);
      const dayOfWeek = viewStartDate.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
      viewStartDate.setHours(0, 0, 0, 0);
      
      return {
        linkEvidence,
        evidenceType,
        evidenceData,
        startDate,
        endDate,
        visibleWeeks,
        weekWidth,
        taskWidth: width,
        taskXPosition: position.x,
        viewStartDate: viewStartDate // Füge viewStartDate hinzu für korrekte Positionierung
      };
    })();
    
    return result;
  }, [user?.isLearner, linkEvidence, evidenceType, startDate, endDate, visibleWeeks, weekWidth, width, position.x, evidenceData]);

  // Badge-Daten für Lehrkräfte dynamisch laden (immer aufrufen für Hook-Regel)
  const { 
    userBadgeData: rawUserBadgeData, 
    loading: rawBadgeLoading, 
    error: rawBadgeError 
  } = useMoodleBadgeFlags(taskDataForBadges, learnerStudentData);
  
  // Für Lernende die Hook-Ergebnisse ignorieren (sie bekommen Daten vom Backend)
  const userBadgeData = user?.isLearner ? null : rawUserBadgeData;
  const badgeLoading = user?.isLearner ? false : rawBadgeLoading;
  const badgeError = user?.isLearner ? null : rawBadgeError;
  


  // Importiere Hilfsfunktionen aus MoodleBadgeFlags
  const getBadgeColor = (badgeId) => {
    const colors = [
      '#4caf50', // Grün
      '#2196f3', // Blau
      '#ff9800', // Orange
      '#9c27b0', // Lila
      '#f44336', // Rot
      '#00bcd4', // Cyan
      '#795548', // Braun
      '#607d8b'  // Blaugrau
    ];
    
    return colors[badgeId % colors.length];
  };

  const getBadgeColorFromTaskData = useCallback((badgeId, taskBadges) => {
    if (!taskBadges || !Array.isArray(taskBadges)) return getBadgeColor(badgeId);
    
    const configuredBadge = taskBadges.find(badge => 
      badge.id === badgeId || badge.id === badgeId.toString()
    );
    
    return configuredBadge?.flagColor || getBadgeColor(badgeId);
  }, []);

  const findOverlappingBadges = (flags, threshold = 30) => {
    const overlappingGroups = [];
    const processed = new Set();
    
    flags.forEach((flag, index) => {
      if (processed.has(index)) return;
      
      const group = [index];
      processed.add(index);
      
      // Finde alle Badges, die mit dem aktuellen Badge überlappen
      flags.forEach((otherFlag, otherIndex) => {
        if (otherIndex === index || processed.has(otherIndex)) return;
        
        const distance = Math.abs(flag.position - otherFlag.position);
        if (distance <= threshold) {
          group.push(otherIndex);
          processed.add(otherIndex);
        }
      });
      
      if (group.length > 1) {
        overlappingGroups.push(group);
      }
    });
    
    return overlappingGroups;
  };

  const calculateCirclePositions = (centerX, centerY, radius, count, startAngle = 0) => {
    const positions = [];
    const angleStep = (2 * Math.PI) / count;
    
    for (let i = 0; i < count; i++) {
      const angle = startAngle + i * angleStep;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      positions.push({ x, y });
    }
    
    return positions;
  };

  // State für Badge-Interaktion (vereinfacht)
  const [clickedGroup, setClickedGroup] = React.useState(null);
  const [clickPosition, setClickPosition] = React.useState({ x: 0, y: 0 });
  const containerRef = React.useRef(null);



  // Handle badge click für Fan-Out Effekt (wie bei MoodleBadgeFlags)
  const handleBadgeClick = React.useCallback((e, groupIndex, flag) => {
    e.stopPropagation();
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickPos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
      
      if (clickedGroup === groupIndex) {
        // Wenn die gleiche Gruppe bereits aktiv ist, schließe sie
        setClickedGroup(null);
      } else {
        // Aktiviere neue Gruppe
        setClickedGroup(groupIndex);
        setClickPosition(clickPos);
      }
    }
  }, [clickedGroup]);
  
  // Handle container click um Fan-Out zu schließen (wie bei MoodleBadgeFlags)
  const handleContainerClick = React.useCallback((e) => {
    // Schließe Fan-Out wenn irgendwo im Container geklickt wird
    if (clickedGroup !== null) {
      setClickedGroup(null);
    }
  }, [clickedGroup]);
  
  // Globaler Event-Listener um Fan-Out bei Klicks außerhalb zu schließen
  React.useEffect(() => {
    const handleGlobalClick = (e) => {
      if (clickedGroup !== null && containerRef.current && !containerRef.current.contains(e.target)) {
        setClickedGroup(null);
      }
    };
    
    if (clickedGroup !== null) {
      document.addEventListener('click', handleGlobalClick);
      return () => document.removeEventListener('click', handleGlobalClick);
    }
  }, [clickedGroup]);

  // Berechne Badge-Positionen für Lehrpersonen (dynamisches Laden)
  const calculateLearnerBadgesNow = useMemo(() => {
    // Diese Funktion ist nur für Lehrpersonen gedacht
    if (user?.isLearner || !linkEvidence || evidenceType !== 'moodle-badge' || !userBadgeData || userBadgeData.length === 0 || !learnerStudentData.length) {
      return [];
    }
    
    // Berechne taskDataForBadges inline
    const viewStartDate = new Date(startDate);
    const dayOfWeek = viewStartDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
    viewStartDate.setHours(0, 0, 0, 0);

    const student = learnerStudentData[0];
    const badgeIds = evidenceData?.badges?.map(b => b.id) || [];
    
    // Verwende die gleiche Benutzer-Matching-Logik wie in MoodleBadgeFlags.js
    const matchingUser = userBadgeData.find(user => {
      const studentEmail = student.email;
      const studentLogin = student.login;
      
      if (studentEmail) {
        const normalizedStudentEmail = studentEmail.toLowerCase();
        const normalizedUserEmail = (user.email || '').toLowerCase();
        const normalizedUserUsername = (user.username || '').toLowerCase();
        
        if (normalizedUserEmail === normalizedStudentEmail || 
            normalizedUserUsername === normalizedStudentEmail) {
          return true;
        }
      }
      
      if (studentLogin && !studentEmail) {
        const normalizedStudentLogin = studentLogin.toLowerCase();
        const normalizedUserEmail = (user.email || '').toLowerCase();
        const normalizedUserUsername = (user.username || '').toLowerCase();
        
        return normalizedUserEmail === normalizedStudentLogin || 
               normalizedUserUsername === normalizedStudentLogin;
      }
      
      return false;
    });

    if (!matchingUser || !matchingUser.badges) {
      return [];
    }

    // Filtere relevante Badges
    const relevantBadges = matchingUser.badges.filter(badge => 
      badgeIds.includes(badge.badgeId) || badgeIds.includes(badge.badgeId.toString())
    );

    // Erstelle Badge-Objekte mit absoluter Positionierung (wie bei MoodleBadgeFlags)
    const taskStartDate = startDate instanceof Date ? startDate : new Date(startDate);
    const taskEndDate = endDate instanceof Date ? endDate : new Date(endDate);
    
    return relevantBadges.map((badge, index) => {
      const issuedDate = badge.issuedDate instanceof Date ? badge.issuedDate : new Date(badge.issuedDate);
      
      // === KORREKTE POSITIONIERUNG WIE IN MoodleBadgeFlags ===
      // Verwende die gleiche Positionslogik wie in MoodleBadgeFlags
      const msPerWeek = 7 * 24 * 60 * 60 * 1000;
      const pixelsPerMs = weekWidth / msPerWeek;
      
      // Badge-Position relativ zum Wochen-Raster
      const badgeOffsetMs = issuedDate.getTime() - viewStartDate.getTime();
      const badgePositionPx = badgeOffsetMs * pixelsPerMs;
      
      // Task-Start-Position relativ zum Wochen-Raster  
      const relativeTaskStartOffsetMs = taskStartDate.getTime() - viewStartDate.getTime();
      const taskStartPositionPx = relativeTaskStartOffsetMs * pixelsPerMs;
      
      // Badge-Position relativ zur MovableBar (0 = linker Rand der MovableBar)
      const positionPixels = badgePositionPx - taskStartPositionPx;
      
      return {
        id: `${student.id}-${badge.badgeId}`,
        badgeId: badge.badgeId,
        badgeName: badge.badgeName,
        position: positionPixels, // Absolute Position in Pixeln
        date: issuedDate,
        isWithinTaskPeriod: issuedDate >= taskStartDate && issuedDate <= taskEndDate,
        color: getBadgeColorFromTaskData(badge.badgeId, evidenceData?.badges),
        type: 'moodle-badge',
        studentId: student.id,
        imageUrl: badge.imageUrl,
        description: `Badge "${badge.badgeName}" erhalten am ${issuedDate.toLocaleDateString('de-DE')}`,
        isReal: true,
        expireDate: badge.expireDate ? (badge.expireDate instanceof Date ? badge.expireDate : new Date(badge.expireDate)) : null
      };
    });
  }, [user?.isLearner, linkEvidence, evidenceType, userBadgeData, learnerStudentData, evidenceData, startDate, endDate, weekWidth, getBadgeColorFromTaskData]);

  // Für Lernende: Verwende die vom Backend vorbereiteten Badge-Daten direkt
  const displayBadges = useMemo(() => {
    return user?.isLearner ? (learnerBadges || []) : calculateLearnerBadgesNow;
  }, [user?.isLearner, learnerBadges, calculateLearnerBadgesNow]);

  // Finde überlappende Badge-Gruppen
  const overlappingGroups = React.useMemo(() => {
    if (!displayBadges || displayBadges.length === 0) return [];
    return findOverlappingBadges(displayBadges, 25) || [];
  }, [displayBadges]);

  // Formatiere Datum für Anzeige
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  // Berechne Fälligkeitsdatum
  const calculateDueDate = useCallback(() => {
    if (!startDate || !evidenceDueDays) return null;
    const dueDate = new Date(startDate);
    dueDate.setDate(dueDate.getDate() + parseInt(evidenceDueDays));
    return dueDate;
  }, [startDate, evidenceDueDays]);

  // Berechne X-Position des Flags basierend auf Fälligkeitsdatum
  const calculateFlagPosition = useCallback(() => {
    const dueDate = calculateDueDate();
    if (!dueDate || !startDate || !endDate) return 50; // Fallback: Mitte des Balkens
    
    // Berechne die relative Position innerhalb des Balkens
    const totalDuration = endDate.getTime() - startDate.getTime();
    const dueOffset = dueDate.getTime() - startDate.getTime();
    
    // Prozentuale Position innerhalb des Balkens (0-100%)
    const relativePosition = Math.max(0, Math.min(100, (dueOffset / totalDuration) * 100));
    
    return relativePosition;
  }, [calculateDueDate, startDate, endDate]);

  // Berechne neues Fälligkeitsdatum basierend auf Flag-Position
  const calculateDueDateFromPosition = useCallback((position) => {
    if (!startDate || !endDate) return null;
    
    // Berechne die Zeit basierend auf der Position (0-100%)
    const totalDuration = endDate.getTime() - startDate.getTime();
    const dueOffset = (position / 100) * totalDuration;
    
    const newDueDate = new Date(startDate.getTime() + dueOffset);
    return newDueDate;
  }, [startDate, endDate]);

  // Aktualisiere evidenceDueDays basierend auf Flag-Position
  const updateEvidenceDueDaysFromPosition = useCallback((position) => {
    const newDueDate = calculateDueDateFromPosition(position);
    if (!newDueDate || !startDate) return;
    
    // Berechne die Anzahl der Tage zwischen Start und Fälligkeitsdatum
    const daysDifference = Math.round((newDueDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    
    // Aktualisiere evidenceDueDays über die Parent-Komponente
    if (onUpdate) {
      onUpdate(id, { evidenceDueDays: Math.max(0, daysDifference) });
    }
  }, [calculateDueDateFromPosition, startDate, onUpdate, id]);

  // Berechne Flaggenfarbe basierend auf Badge-Status
  const calculateFlagColor = useCallback(() => {
    // Nur für Lernende mit Badges aktiv
    if (!user?.isLearner || !displayBadges || displayBadges.length === 0 || !linkEvidence) {
      return 'blue'; // Standardfarbe für Lehrpersonen und ohne Badges
    }

    const dueDate = calculateDueDate();
    if (!dueDate) {
      return 'blue'; // Fallback wenn kein Fälligkeitsdatum vorhanden
    }

    const currentDate = new Date();
    
    // Filtere nur die relevanten Badges für diesen Task
    const taskBadges = displayBadges.filter(badge => {
      // Prüfe ob das Badge für diesen Task relevant ist
      const badgeIds = evidenceData?.badges?.map(b => b.id || b.badgeId) || [];
      return badgeIds.includes(badge.badgeId) || badgeIds.includes(badge.badgeId?.toString());
    });

    // Wenn keine Badges vorhanden sind
    if (taskBadges.length === 0) {
      // Prüfe ob das Fälligkeitsdatum überschritten ist
      if (currentDate > dueDate) {
        return 'red'; // Überfällig - kein Badge und Datum überschritten
      }
      return 'blue'; // Noch nicht fällig
    }

    // Prüfe Badge-Zeitpunkte relativ zum Fälligkeitsdatum
    let hasEarlyBadge = false; // Badge vor Fälligkeitsdatum
    let hasLateBadge = false;  // Badge nach Fälligkeitsdatum

    taskBadges.forEach(badge => {
      const badgeDate = badge.date instanceof Date ? badge.date : new Date(badge.date);
      
      if (badgeDate <= dueDate) {
        hasEarlyBadge = true;
      } else {
        hasLateBadge = true;
      }
    });

    // Logik für Flaggenfarbe:
    // 1. Grün: Mindestens ein Badge vor/am Fälligkeitsdatum (vorzeitig)
    // 2. Orange: Alle Badges nach dem Fälligkeitsdatum (verspätet)
    if (hasEarlyBadge) {
      return 'green'; // Badge vorzeitig erhalten
    } else if (hasLateBadge) {
      return 'orange'; // Badge nach Zieldatum erhalten
    }

    return 'blue'; // Fallback
  }, [user?.isLearner, displayBadges, linkEvidence, calculateDueDate, evidenceData?.badges]);

  // Erstelle Tooltip-Text für Evidence Flag
  const getEvidenceTooltip = () => {
    const dueDate = calculateDueDate();
    let tooltip = evidenceLabel || 'Nachweis';
    if (dueDate) {
      tooltip += `\nFällig am: ${formatDate(dueDate)}`;
    }

    // Für Lernende: Badge-Status in Tooltip anzeigen
    if (user?.isLearner && displayBadges && displayBadges.length > 0 && linkEvidence) {
      const flagColor = calculateFlagColor();
      const colorStatus = {
        'green': 'Badge vorzeitig erhalten',
        'orange': 'Badge nach Zieldatum erhalten', 
        'red': 'Badge überfällig',
        'blue': 'Noch nicht fällig'
      };
      tooltip += `\nStatus: ${colorStatus[flagColor] || 'Unbekannt'}`;
    }

    return tooltip;
  };

  // Erstelle Tooltip-Text
  const getTooltipText = () => {
    if (startDate && endDate) {
      return `${label}\nVon: ${formatDate(startDate)}\nBis: ${formatDate(endDate)}`;
    }
    return label;
  };

  const handleMouseDown = (e) => {
    if (!isEditing) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    if (e.target.classList.contains('resize-handle') || 
        e.target.classList.contains('move-arrow') || 
        e.target.classList.contains('evidence-flag')) return;
    
    // Alle Events sofort stoppen
    e.preventDefault();
    e.stopPropagation();
    
    // Zusätzliche Sicherheit für Touch/Pointer Events
    if (e.nativeEvent) {
      e.nativeEvent.preventDefault();
      e.nativeEvent.stopPropagation();
    }
    
    // Nur noch horizontales Dragging erlauben
    setIsDragging(true);
    if (barRef.current && barRef.current.parentElement) {
      const parentWidth = barRef.current.parentElement.offsetWidth;
      setDragStart({
        x: e.clientX - (position.x * parentWidth / 100),
        y: e.clientY - position.y
      });
    }
    
    return false; // Zusätzliche Sicherheit
  };

  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isDragging && barRef.current && barRef.current.parentElement) {
      const parentWidth = barRef.current.parentElement.offsetWidth;
      const newXPercent = ((e.clientX - dragStart.x) / parentWidth) * 100;
      const clampedX = Math.max(0, Math.min(100 - (width), newXPercent));
      
      currentPositionRef.current = { x: clampedX, y: position.y };
      setPosition({
        x: clampedX,
        y: position.y // Y-Position bleibt konstant beim horizontalen Drag
      });
    }
    
    if (isResizing && barRef.current && barRef.current.parentElement) {
      const parentWidth = barRef.current.parentElement.offsetWidth;
      const parentLeft = barRef.current.parentElement.getBoundingClientRect().left;
      // Die Mausposition relativ zum linken Rand des Parent-Containers
      const mouseX = e.clientX - parentLeft;
      // Die neue Breite ist die Differenz zwischen Mausposition und linker Balkenrand
      const newWidthPercent = (mouseX / parentWidth) * 100 - position.x;
      // Mindestbreite von ca. 1 Tag (etwa 0.5% bei 10-20 sichtbaren Wochen)
      const clampedWidth = Math.max(0.5, Math.min(100 - position.x, newWidthPercent)); 
      currentWidthRef.current = clampedWidth;
      setWidth(clampedWidth);
    }
    
    if (isDraggingFlag && barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      const relativeX = e.clientX - rect.left;
      const newPosition = Math.max(0, Math.min(100, (relativeX / rect.width) * 100));
      
      setFlagPosition(newPosition);
      flagPositionRef.current = newPosition; // Auch den Ref aktualisieren
    }
  }, [isDragging, dragStart.x, width, position.y, isResizing, position.x, isDraggingFlag]);

  const handleMouseUp = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Informiere Parent über finale Änderungen nach dem Drag/Resize
    if ((isDragging || isResizing) && onUpdate) {
      if (isDragging) {
        onUpdate(id, { x: currentPositionRef.current.x });
      }
      if (isResizing) {
        onUpdate(id, { width: currentWidthRef.current });
        
        // Nach dem Resize prüfen, ob die Flagge noch innerhalb des Tasks liegt
        if (linkEvidence && startDate && endDate) {
          const taskDuration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // Wenn evidenceDueDays größer als die Task-Dauer ist, anpassen
          if (evidenceDueDays > taskDuration) {
            onUpdate(id, { evidenceDueDays: taskDuration });
          }
        }
      }
    }
    
    if (isDraggingFlag) {
      // Aktualisiere evidenceDueDays basierend auf neuer Flag-Position
      updateEvidenceDueDaysFromPosition(flagPositionRef.current);
      
      // Kleine Verzögerung bevor wir isDraggingFlag zurücksetzen
      // Das verhindert Race Conditions mit dem useEffect
      setTimeout(() => {
        setIsDraggingFlag(false);
      }, 10);
    } else {
      setIsDragging(false);
      setIsResizing(false);
    }
    
    if (!isDraggingFlag) {
      setIsDragging(false);
      setIsResizing(false);
    }
  }, [isDragging, isResizing, onUpdate, id, linkEvidence, startDate, endDate, evidenceDueDays, isDraggingFlag, updateEvidenceDueDaysFromPosition]);

  // Vertikale Verschiebung mit Pfeil nach oben
  const handleMoveUp = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Expliziter Tausch mit direktem Nachbarn
    if (onMoveUp) {
      onMoveUp(id);
    }
  };

  // Vertikale Verschiebung mit Pfeil nach unten
  const handleMoveDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Rufe die onMoveDown Callback-Funktion auf
    if (onMoveDown) {
      onMoveDown(id);
    }
  };

  // Resize functionality
  const handleResizeMouseDown = (e) => {
    if (!isEditing) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    
    // Alle Events sofort stoppen
    e.preventDefault();
    e.stopPropagation();
    
    // Zusätzliche Sicherheit für Touch/Pointer Events
    if (e.nativeEvent) {
      e.nativeEvent.preventDefault();
      e.nativeEvent.stopPropagation();
    }
    
    setIsResizing(true);
    return false; // Zusätzliche Sicherheit
  };

  
  // Copy-Funktionalität - Jetzt mit CopyMoveDialog
  const handleCopyTask = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Erstelle ein Task-Objekt für das CopyMoveDialog
    const taskData = {
      id: `${panelId}_${id}`, // Eindeutige ID
      barId: id,
      uuid: uuid, // UUID für das Kopieren der Zuordnungen
      panelId: panelId,
      panelTitle: panelTitle || 'Unbenanntes Panel',
      panelGroupTitle: panelGroupTitle || 'Unbenannte Gruppe',
      label,
      textColor,
      backgroundColor,
      borderStyle,
      borderColor,
      borderThickness,
      startDate,
      endDate,
      linkEvidence,
      evidenceType,
      evidenceLabel,
      evidenceDueDays,
      evidenceData,
      assignedStudents: assignedStudents || [],
      x: position.x,
      y: position.y,
      width: width
    };
    
    // Öffne das neue CopyMoveDialog
    openCopyMoveDialog(taskData);
  };

  // Edit Dialog Funktionen
  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) {
      onEdit(id, {
        label,
        textColor,
        backgroundColor,
        borderStyle,
        borderColor,
        borderThickness,
        startDate,
        endDate,
        linkEvidence,
        evidenceType,
        evidenceLabel,
        evidenceDueDays,
        evidenceData
      });
    }
  };

  // Evidence Link Funktionen
  const handleEvidenceClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Nur klicken wenn nicht im Bearbeitungsmodus oder nicht gerade gedraggt wird
    if (isEditing || isDraggingFlag) return;
    
    // Für manuelle Nachweise und Moodle Badges prüfen wir ob ein Link vorhanden ist
    if ((evidenceType === 'manual' || evidenceType === 'moodle-badge') && evidenceData && evidenceData.link && evidenceData.link.trim() !== '') {
      // Für manuelle Nachweise zusätzlich prüfen ob Link optional ist
      if (evidenceType === 'manual' && evidenceData.linkOptional) {
        // Fallback für optionale Links
        window.open('https://moodle.bws-uster.ch', '_blank');
      } else {
        window.open(evidenceData.link, '_blank');
      }
    } else {
      // Fallback für andere Evidence-Typen
      window.open('https://moodle.bws-uster.ch', '_blank');
    }
  };

  // Flag Drag-Handler
  const handleFlagMouseDown = (e) => {
    if (!isEditing) {
      // Im Nicht-Bearbeitungsmodus normal klicken
      handleEvidenceClick(e);
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsDraggingFlag(true);
    
    // Wichtig: Event-Propagation für das Haupt-Element stoppen
    if (e.nativeEvent) {
      e.nativeEvent.preventDefault();
      e.nativeEvent.stopPropagation();
    }
    
    return false;
  };

  const handleFlagClick = (e) => {
    // Nur im Nicht-Bearbeitungsmodus als Click behandeln
    if (!isEditing && isEvidenceLinkActive()) {
      handleEvidenceClick(e);
    }
  };

  // Prüfe ob das Evidence-Icon klickbar sein soll
  const isEvidenceLinkActive = () => {
    // Für manuelle Nachweise prüfen wir die evidenceData
    if (evidenceType === 'manual') {
      if (!evidenceData || typeof evidenceData !== 'object') {
        return false; // Keine evidenceData vorhanden
      }
      const hasValidLink = evidenceData.link && evidenceData.link.trim() !== '';
      const isNotOptional = !evidenceData.linkOptional;
      return hasValidLink && isNotOptional;
    }
    
    // Für Moodle Badges prüfen wir ob ein Link vorhanden ist
    if (evidenceType === 'moodle-badge') {
      if (!evidenceData || typeof evidenceData !== 'object') {
        return false; // Keine evidenceData vorhanden
      }
      return evidenceData.link && evidenceData.link.trim() !== '';
    }
    
    // Für andere Evidence-Typen ist es immer aktiv
    return true;
  };

  // Mouse event listeners
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleMouseMove(e);
    };

    const handleGlobalMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleMouseUp(e);
    };

    if (isDragging || isResizing || isDraggingFlag) {
      // Verwende window statt document für bessere Kompatibilität
      window.addEventListener('mousemove', handleGlobalMouseMove, { capture: true, passive: false });
      window.addEventListener('mouseup', handleGlobalMouseUp, { capture: true, passive: false });
      
      // Zusätzlich Pointer Events
      window.addEventListener('pointermove', handleGlobalMouseMove, { capture: true, passive: false });
      window.addEventListener('pointerup', handleGlobalMouseUp, { capture: true, passive: false });
      
      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove, { capture: true });
        window.removeEventListener('mouseup', handleGlobalMouseUp, { capture: true });
        window.removeEventListener('pointermove', handleGlobalMouseMove, { capture: true });
        window.removeEventListener('pointerup', handleGlobalMouseUp, { capture: true });
      };
    }
  }, [isDragging, isResizing, isDraggingFlag, handleMouseMove, handleMouseUp]);

  // Update position and width when props change
  useEffect(() => {
    setPosition({ x: initialX, y: initialY });
    setWidth(initialWidth);
    currentPositionRef.current = { x: initialX, y: initialY };
    currentWidthRef.current = initialWidth;
    setLabel(initialLabel);
    
    // Reset drag/resize state to prevent stuck drag after sorting or prop changes
    setIsDragging(false);
    setIsResizing(false);
    setIsDraggingFlag(false);
    setDragStart({ x: 0, y: 0 });
  }, [initialX, initialY, initialWidth, initialLabel, textColor, backgroundColor, borderStyle, borderColor, borderThickness]);

  // Separater useEffect für Flag-Position - nur abhängig von Datum-relevanten Änderungen
  useEffect(() => {
    // Aktualisiere Flag-Position nur wenn sich evidenceDueDays extern geändert hat
    // (nicht durch unser eigenes Drag & Drop) oder wenn sich Start-/Enddatum geändert hat
    if (!isDraggingFlag && (
      evidenceDueDays !== lastEvidenceDueDaysRef.current || 
      startDate || endDate // Neu berechnen wenn sich Datum ändert
    )) {
      const newFlagPosition = calculateFlagPosition();
      setFlagPosition(newFlagPosition);
      flagPositionRef.current = newFlagPosition;
      lastEvidenceDueDaysRef.current = evidenceDueDays;
    }
  }, [startDate, endDate, evidenceDueDays, isDraggingFlag, calculateFlagPosition]);

  // Separater useEffect nur für die erste Initialisierung
  useEffect(() => {
    // Nur beim ersten Mount setzen
    if (startDate && endDate) {
      const initialFlagPosition = calculateFlagPosition();
      setFlagPosition(initialFlagPosition);
      flagPositionRef.current = initialFlagPosition;
      lastEvidenceDueDaysRef.current = evidenceDueDays;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Leere Dependency-Liste - nur einmal beim Mount

  return (
    <div
      ref={barRef}
      style={{
        position: 'absolute',
        left: `${position.x}%`, // Verwendung von Prozent statt Pixel
        top: `${position.y}px`,
        width: `${width}%`, // Verwendung von Prozent statt Pixel
        height: '28px', // Kleinere Höhe
        backgroundColor: backgroundColor,
        border: `${borderThickness}px ${borderStyle} ${borderColor}`,
        borderRadius: '4px', // Kleinerer Radius
        cursor: isEditing ? (isDragging ? 'ew-resize' : 'grab') : 'default', // Nur horizontaler Cursor
        display: 'flex',
        alignItems: 'center',
        padding: '0px 0px', // Kein x-Padding
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)', // Kleinerer Schatten
        userSelect: 'none',
        zIndex: 9999, // Sehr hoher z-index
        pointerEvents: 'auto' // Explizit pointer events aktivieren
      }}
      title={getTooltipText()} // Einfacher Tooltip ohne Drag-Informationen
      onMouseDown={handleMouseDown}
      onPointerDown={handleMouseDown} // Zusätzlich Pointer Events
      onMouseUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
      onPointerUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
      onMouseMove={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Up/Down Buttons Container - nur im Edit-Modus sichtbar */}
      {isEditing && (!isFirst || !isLast) && (
        <div
          style={{
            position: 'absolute',
            left: showSelection ? '77px' : '53px', // Verschiebe wenn Checkbox sichtbar ist (77px = 53px + 20px + 4px Abstand)
            top: '3px', // Etwas höher für zentrierte Ausrichtung
            display: 'flex',
            flexDirection: 'column',
            gap: isFirst || isLast ? '0px' : '2px', // Kein Gap wenn nur ein Button
            zIndex: 99999,
          }}
        >
          {/* Pfeil nach oben - nur wenn nicht das oberste Element */}
          {!isFirst && (
            <button
              className="move-arrow"
              onClick={handleMoveUp}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              style={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#007bff',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: isLast ? '3px' : '3px 3px 0 0', // Wenn letztes Element, alle Ecken rund
                cursor: 'pointer',
                padding: '2px 6px', // Reduziertes vertikales Padding
                fontSize: '10px', // Etwas kleinere Schrift
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
                height: '10px', // Feste Höhe für gleichmäßige Verteilung
                width: '24px', // Gleiche Breite wie ein normaler Button
                borderBottom: isLast ? '1px solid rgba(0,0,0,0.1)' : 'none' // Border nur wenn letztes Element
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,1)';
                e.target.style.color = '#0056b3';
                e.target.style.transform = 'scale(1.05)'; // Weniger Scale für kleinere Buttons
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.9)';
                e.target.style.color = '#007bff';
                e.target.style.transform = 'scale(1)';
              }}
              title="Nach oben verschieben"
            >
              ▲
            </button>
          )}
          
          {/* Platzhalter für obersten Balken - unsichtbar aber behält Layout */}
          {isFirst && !isLast && (
            <div style={{ height: '12px', width: '24px' }} />
          )}

          {/* Pfeil nach unten - nur wenn nicht das unterste Element */}
          {!isLast && (
            <button
              className="move-arrow"
              onClick={handleMoveDown}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onPointerUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              style={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#007bff',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: isFirst ? '3px' : '0 0 3px 3px', // Wenn erstes Element, alle Ecken rund
                cursor: 'pointer',
                padding: '2px 6px', // Reduziertes vertikales Padding
                fontSize: '10px', // Etwas kleinere Schrift
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
                height: '10px', // Feste Höhe für gleichmäßige Verteilung
                width: '24px', // Gleiche Breite wie ein normaler Button
                borderTop: isFirst ? '1px solid rgba(0,0,0,0.1)' : 'none' // Border nur wenn erstes Element
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,1)';
                e.target.style.color = '#0056b3';
                e.target.style.transform = 'scale(1.05)'; // Weniger Scale für kleinere Buttons
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255,255,255,0.9)';
                e.target.style.color = '#007bff';
                e.target.style.transform = 'scale(1)';
              }}
              title="Nach unten verschieben"
            >
              ▼
            </button>
          )}
        </div>
      )}

      {/* Selection Checkbox - nur wenn showSelection aktiv ist */}
      {showSelection && isEditing && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onSelectionChange) {
              onSelectionChange(id, !isSelected);
            }
          }}
          style={{
            position: 'absolute',
            left: '2px',
            top: '2px',
            width: '23px',
            height: '23px',
            cursor: 'pointer',
            zIndex: 10000,
            backgroundColor: 'white',
            border: '1px solid #6363636e',
            borderRadius: '3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box'
          }}
          title="Task für Batch-Operationen auswählen"
        >
          {isSelected && (
            <span style={{
              color: '#ff1919ff',
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              ✓
            </span>
          )}
        </div>
      )}

      {/* Edit button - nur im Edit-Modus sichtbar */}
      {isEditing && (
        <button
          onClick={handleEditClick}
          style={{
            background: 'rgba(255,255,255,0.9)', // Weniger transparent für bessere Lesbarkeit
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '3px',
            color: '#333333', // Dunkle Farbe für besseren Kontrast
            cursor: 'pointer',
            padding: '4px 6px',
            marginLeft: '0px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position:'absolute',
            left: showSelection ? '26px' : '2px', // Verschiebe wenn Checkbox sichtbar ist (26px = 22px + 4px Abstand)
            zIndex: 10000,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)' // Leichter Schatten für Tiefe
          }}
          title="Nachweis bearbeiten"
        >
          <FiEdit3 />
        </button>
      )}

      {/* Copy button - nur im Edit-Modus sichtbar */}
      {isEditing && (
        <button
          onClick={handleCopyTask}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          style={{
            background: 'rgba(255,255,255,0.9)', // Weniger transparent für bessere Lesbarkeit
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '3px',
            color: '#333333', // Dunkle Farbe für besseren Kontrast
            cursor: 'pointer',
            padding: '4px 6px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position:'absolute',
            left: showSelection ? '51px' : '27px', // Verschiebe wenn Checkbox sichtbar ist (51px = 26px + 25px = 22px + 4px + 25px)
            zIndex: 10000,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)' // Leichter Schatten für Tiefe
          }}
          title="Nachweis kopieren oder verschieben"
        >
          <FiCopy />
        </button>
      )}

      {/* Info/Close button - nur für Lehrpersonen sichtbar, nicht für Lernende */}
      {!isEditing && user && !user.isLearner && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (onToggleInfoPanel) onToggleInfoPanel();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          style={{
            background: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '3px',
            color: showInfoPanel ? '#007bff' : '#333333',
            cursor: 'pointer',
            padding: '4px 6px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position:'absolute',
            left: showSelection && isEditing ? '26px' : '2px', // Verschiebe wenn Checkbox sichtbar ist (26px = 22px + 4px Abstand)
            zIndex: 10000,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
          title={showInfoPanel ? "InfoPanel schließen" : "Taskfortschritte anzeigen"}
        >
          {showInfoPanel ? '▲' : <FiUsers />}
        </button>
      )}

      {/* Evidence Flag - nur wenn linkEvidence aktiviert ist und nicht während Resize */}
      {linkEvidence && !isResizing && (
        <button
          className="evidence-flag"
          onMouseDown={handleFlagMouseDown}
          onPointerDown={handleFlagMouseDown}
          onClick={handleFlagClick}
          onMouseUp={(e) => {
            if (isEditing) {
              e.preventDefault();
              e.stopPropagation();
            }
            return false;
          }}
          onPointerUp={(e) => {
            if (isEditing) {
              e.preventDefault();
              e.stopPropagation();
            }
            return false;
          }}
          style={{
            background: user?.isLearner ? calculateFlagColor() : 'rgba(255,255,255,0.9)',
            border:  '1px solid rgba(0,0,0,0.5)',
            borderRadius: '50%',
            color: user?.isLearner ? 'white' : (isEvidenceLinkActive() ? 'blue' : 'black'),
            cursor: isEditing ? (isDraggingFlag ? 'grabbing' : 'grab') : (isEvidenceLinkActive() ? 'pointer' : 'default'),
            padding: '2px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            left: `${flagPosition}%`,
            top: '-8px', // Ragt nach oben aus dem Balken heraus
            transform: 'translateX(-50%)', // Zentriert das Icon auf der berechneten Position
            zIndex: 10001,
            width: '24px',
            height: '24px',
            boxShadow: isDraggingFlag ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.2)',
            opacity: 1,
            transition: isDraggingFlag ? 'none' : 'all 0.2s ease',
            userSelect: 'none'
          }}
          title={isEditing ? `${getEvidenceTooltip()}\n\nIm Bearbeitungsmodus: Ziehen um Fälligkeitstermin zu ändern` : getEvidenceTooltip()}
        >
          <FiFlag />
        </button>
      )}
      {/* Label */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {isEditingLabel ? (
          <input
            ref={labelRef}
            type="text"
            value={label}

            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              color: textColor,
              fontSize: '12px', // Kleinere Schrift
              fontWeight: 'bold',
              width: '100%',
              outline: 'none'
            }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <span

              style={{
                color: textColor,
                fontSize: '12px', // Kleinere Schrift
                fontWeight: 'bold',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.1'
              }}
            >
              {label}
            </span>
            {startDate && endDate && (
              <span style={{
                color: `${textColor}cc`, // Leicht transparente Version der Textfarbe
                fontSize: '9px',
                lineHeight: '1.1',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {formatDate(startDate)} - {formatDate(endDate)}
              </span>
            )}
            
          </div>
        )}
      </div>

      
      

      {/* Resize handle - nur im Edit-Modus sichtbar */}
      {isEditing && (
        <div
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          onPointerDown={handleResizeMouseDown}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onMouseUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          onPointerUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }}
          style={{
            position: 'absolute',
            right: '0',
            top: '0',
            width: '8px', // Schmalerer Handle
            height: '100%',
            cursor: 'ew-resize',
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '0 3px 3px 0', // Kleinerer Radius
            zIndex: 10000, // Noch höher als der Balken
            pointerEvents: 'auto'
        }}
      />
      )}

      {/* Badge-Anzeige für Lernende - im gleichen Container wie Panels */}
      {user?.isLearner && displayBadges && displayBadges.length > 0 && (
        <div 
          ref={containerRef}
          className="moodle-badge-container"
          style={{ 
            position: 'absolute', // Relative zum Panel-Container (scrollt mit)
            top: '0px', // Y relativ zur MovableBar
            left: '0px', // X relativ zur MovableBar  
            width: '100%', // Volle Breite des Panel-Containers
            height: '50px', // MovableBar-Höhe
            pointerEvents: 'none', // Container selbst nicht klickbar
            overflow: 'visible',
            zIndex: 999999 // Höchste Priorität im lokalen Stacking Context
          }}
          onClick={handleContainerClick}
        >
          {displayBadges.map((flag, index) => {
            // Normalisiere Badge-Datum (kann String, Timestamp oder Date sein)
            const flagDate = flag.date instanceof Date ? flag.date : new Date(flag.date);
            
            // Prüfe Badge-Status (identisch zu MoodleBadgeFlags)
            const taskStartDate = startDate instanceof Date ? startDate : new Date(startDate);
            const taskEndDate = endDate instanceof Date ? endDate : new Date(endDate);
            const isLate = flagDate > taskEndDate;
            const isEarly = flagDate < taskStartDate;
            const isOutsideTaskPeriod = !flag.isWithinTaskPeriod;
            const isOutside = isOutsideTaskPeriod;
            
            // Prüfe, ob dieses Badge zu einer überlappenden Gruppe gehört
            const overlappingGroupIndex = overlappingGroups ? overlappingGroups.findIndex(group => group.includes(index)) : -1;
            const isInOverlappingGroup = overlappingGroupIndex !== -1;
            const isGroupClicked = clickedGroup === overlappingGroupIndex;
            
            // Berechne X-Position basierend auf visibleWeeks und MovableBar-Position
            const viewStartDate = (() => {
              const date = new Date(startDate);
              const dayOfWeek = date.getDay();
              const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
              date.setDate(date.getDate() + mondayOffset);
              date.setHours(0, 0, 0, 0);
              return date;
            })();
            const msPerWeek = 7 * 24 * 60 * 60 * 1000;
            const pixelsPerMs = weekWidth / msPerWeek;
            
            // Badge-Position relativ zum Task-Start (wie ursprünglich berechnet)
            const badgeOffsetMs = flagDate.getTime() - viewStartDate.getTime();
            const badgePositionInWeeks = badgeOffsetMs * pixelsPerMs;
            
            // MovableBar-Start-Position relativ zum Wochen-Raster (verwende bereits deklarierte Variable)
            const taskStartOffsetMs = taskStartDate.getTime() - viewStartDate.getTime();
            const taskStartPositionPx = taskStartOffsetMs * pixelsPerMs;
            
            // Badge-Position relativ zur MovableBar + MovableBar-Position im Container
            let displayX = position.x + (badgePositionInWeeks - taskStartPositionPx);
            let displayY = 15; // Mitte der MovableBar
            
            if (isInOverlappingGroup && isGroupClicked && overlappingGroups && overlappingGroups[overlappingGroupIndex]) {
              // Fanout-Position - berechne relativ zur Badge-Container-Position
              const group = overlappingGroups[overlappingGroupIndex];
              const positionInGroup = group.indexOf(index);
              const radius = Math.max(15, group.length * 4);
              
              const circlePositions = calculateCirclePositions(
                clickPosition.x, // Click-Position innerhalb des Containers
                clickPosition.y, 
                radius, 
                group.length,
                -Math.PI / 2 // Start oben
              );
              
              displayX = circlePositions[positionInGroup].x;
              displayY = circlePositions[positionInGroup].y;
            }
            
            // Dynamisches Styling (identisch zu MoodleBadgeFlags)
            // Fallback auf dynamische Farb-Berechnung wenn Backend-Farbe fehlt
            const badgeColor = flag.color || getBadgeColorFromTaskData(flag.badgeId, evidenceData?.badges);
            
            let badgeStyle = {
              backgroundColor: badgeColor,
              border: isOutside 
                ? `1px solid ${isEarly ? '#ffc107' : isLate ? '#dc3545' : badgeColor}` 
                : '2px solid white',
              boxShadow: isOutside
                ? isEarly 
                  ? '0 2px 6px rgba(0,0,0,0.3), 0 0 2px 2px rgba(255, 193, 7, 0.8)' 
                  : isLate 
                  ? '0 2px 6px rgba(0,0,0,0.3), 0 0 2px 2px rgba(220, 53, 69, 0.8)'
                  : '0 2px 6px rgba(0,0,0,0.3)'
                : isLate
                ? '0 2px 6px rgba(0,0,0,0.3), 0 0 8px 2px rgba(220, 53, 69, 0.6)'
                : isEarly
                ? '0 2px 6px rgba(0,0,0,0.3), 0 0 8px 2px rgba(255, 193, 7, 0.6)'
                : '0 2px 6px rgba(0,0,0,0.3)'
            };
            
            // Badge-Counter für überlappende Gruppen
            const badgeCount = isInOverlappingGroup ? overlappingGroups[overlappingGroupIndex].length : 1;
            const isFirstInGroup = isInOverlappingGroup ? overlappingGroups[overlappingGroupIndex][0] === index : false;
            const showCounter = badgeCount > 1 && !isGroupClicked && isFirstInGroup;
            
            // Bestimme Icon basierend auf Position
            const badgeIcon = isOutside 
              ? (isEarly ? '⏰' : isLate ? '⚠️' : '🏅')
              : '🏅';
            
            // Bestimme Größe und z-Index
            const badgeSize = isOutside ? '20px' : '18px';
            const baseScale = isOutside ? 1.1 : 1.0;
            let zIndex = isOutside ? 200 : 100; // Relativ niedrig, da Container bereits auf 999999
            
            // Erhöhe z-Index für geklickte Gruppen (Fanout muss über anderen Badges liegen!)
            if (isInOverlappingGroup && isGroupClicked) {
              zIndex += 500; // Fanout-Badges über normale Badges
            }
            
            // Erhöhe z-Index für Badges mit Counter
            if (showCounter) {
              zIndex += 50;
            }

            // Badges der geklickten Gruppe werden im normalen Container mit Fanout-Position gerendert
            // (Entfernung des separaten Fixed-Containers um Positionierungsprobleme zu vermeiden)

            return (
              <div
                key={flag.id}
                className={`moodle-badge ${
                  isOutside 
                    ? (isEarly ? 'moodle-badge--early' : isLate ? 'moodle-badge--late' : 'moodle-badge--outside')
                    : 'moodle-badge--normal'
                } ${
                  isInOverlappingGroup ? 'moodle-badge--overlapping' : ''
                } ${
                  isInOverlappingGroup && isGroupClicked ? 'moodle-badge--fanned-out' : ''
                } ${
                  isOutside ? 'moodle-badge--large' : 'moodle-badge--small'
                }`}
                style={{
                  position: 'absolute',
                  left: `${displayX}px`, // X-Position basiert auf visibleWeeks-Berechnung
                  top: `${displayY}px`,  // Y-Position relativ zur MovableBar
                  transform: isInOverlappingGroup && isGroupClicked 
                    ? `translate(-50%, -50%) scale(${baseScale * 1.3})` 
                    : `translate(-50%, -50%) scale(${baseScale})`,
                  pointerEvents: 'auto', // Badge selbst klickbar machen
                  width: badgeSize,
                  height: badgeSize,
                  ...badgeStyle,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  zIndex: zIndex,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isOutside ? '11px' : '10px',
                  color: 'white',
                  fontWeight: 'bold',
                  transition: isInOverlappingGroup ? 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'all 0.3s ease-out'
                }}
                onClick={(e) => handleBadgeClick(e, overlappingGroupIndex, flag)}
                title={`${flag.badgeName}\nErhalten am: ${flagDate.toLocaleDateString('de-DE')}\n${
                  flag.isWithinTaskPeriod 
                    ? 'Rechtzeitig erhalten' 
                    : isEarly 
                    ? 'Vor Task-Beginn erhalten' 
                    : 'Nach Task-Ende erhalten'
                }`}
              >
                {badgeIcon}
                
                {/* Badge Counter (wie bei Lehrpersonen) */}
                {showCounter && (
                  <div 
                    className={`moodle-badge-counter ${badgeCount > 9 ? 'moodle-badge-counter--large' : ''}`}
                  >
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}



      {/* Badge-Lade-Indikator für Lernende */}
      {user?.isLearner && badgeLoading && linkEvidence && evidenceType === 'moodle-badge' && (
        <div style={{
          position: 'absolute',
          top: '-6px',
          right: '8px',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: '#17a2b8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '8px',
          color: 'white',
          zIndex: 10003,
          animation: 'spin 1s linear infinite',
          pointerEvents: 'none'
        }}>
          ⟳
        </div>
      )}

      {/* Badge-Fehler-Indikator für Lernende */}
      {user?.isLearner && badgeError && linkEvidence && evidenceType === 'moodle-badge' && (
        <div style={{
          position: 'absolute',
          top: '-6px',
          right: '8px',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: '#dc3545',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '8px',
          color: 'white',
          zIndex: 10003,
          cursor: 'help',
          pointerEvents: 'auto'
        }}
        title={`Fehler beim Laden der Badge-Daten: ${badgeError}`}
        >
          !
        </div>
      )}
    </div>
  );
};

export default MovableBar;
